import { useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { axisGridColor, ticksColor } from './color';
import { externalTooltipHandler } from './createCustomTooltip';
import { formatNumber } from 'shared/utils/formatNumber';

const debouncedCreateExternalTooltip = debounce(
  (c, title, tooltipId, isMobile, originalLabels2) =>
    externalTooltipHandler(c, title, tooltipId, isMobile, originalLabels2),
  50,
);

export const useChartModel = ({
  labels,
  data: dataset,
  secondData,
  originalLabels2 = [],
  title,
  color,
  secondColor,
  colorFill,
  secondColorFill,
  isMobile,
}) => {
  const [tooltipId] = useState(uuidv4());

  let lbls = [...labels];
  let ds1 = [...dataset];
  let ds2 = [...secondData];
  let oL2 = [...originalLabels2];

  if (lbls.length === 1 && ds1.length === 1) {
    lbls.push(lbls[0]);
    ds1.push(ds1[0]);
  }

  if (lbls.length === 2 && ds2.length === 1) {
    ds2.push(ds2[0]);
    if (oL2.length === 1) oL2.push(oL2[0]);
  }

  if (labels.length === 2 && secondData.length === 1) {
    // это для того, чтобы показывалась линия на графике, когда у нас одна точка
    secondData.push(secondData[0]);
  }

  const options = useMemo(
    () => ({
      plugins: {
        legend: { display: false, labels: { usePointStyle: true, pointStyle: 'circle' } },
        datalabels: { display: false },
        tooltip: {
          enabled: false,
          external: c => {
            debouncedCreateExternalTooltip(c, title, tooltipId, isMobile, oL2);
          },
          callbacks: {
            label: (context) => formatNumber(context.raw),
            labelColor: ctx => ({
              borderColor: ctx.dataset.borderColor,
              backgroundColor: ctx.dataset.borderColor,
            }),
          },
        },
      },
      scales: {
        x: {
          grid: { borderColor: axisGridColor, borderWidth: 2, color: ticksColor },
          ticks: {
            color: ticksColor,
            font: {
              size: 9,
              family: 'Manrope',
              weight: 'bold',
              lineHeight: 1,
            },
            // maxTicksLimit: 20,
          },
          // afterBuildTicks: (scale) => {
          //   const ticks = scale.ticks;
          //   if (!ticks || ticks.length <= 20) return;

          //   const last = ticks.length - 1;
          //   const step = Math.ceil(ticks.length / 20);


          //   let filtered = ticks.filter((t, i) =>
          //     i === 0 || i === last || i % step === 0
          //   );

          //   if (filtered[filtered.length - 1].value !== ticks[last].value) {
          //     filtered = [...filtered, ticks[last]];
          //   }

          //   scale.ticks = filtered;
          // },
        },
        y: {
          grid: { borderColor: axisGridColor, borderWidth: 2, color: ticksColor },
          ticks: {
            color: ticksColor,
            font: {
              size: 9,
              family: 'Manrope',
              weight: 'bold',
              lineHeight: 1,
            },
          },
        },
      },
      responsive: true,
      aspectRatio: isMobile ? 1 : 2,
    }),
    [tooltipId, title, isMobile, oL2],
  );

  const data = useMemo(
    () => ({
      labels: lbls,
      datasets: [
        {
          type: 'line',
          data: ds1,
          borderColor: color || '#FFFFFF80',
          borderWidth: 1,
          backgroundColor: colorFill || 'rgba(70, 21, 92, 1)',
          fill: true,
          pointLabels: false,
          pointBackgroundColor: '#A16B00',
          pointBorderColor: color || '#A64DFF',
          pointRadius: 4,
          pointHoverRadius: 4,
        },
        {
          type: 'line',
          data: ds2,
          borderColor: secondColor || '#FFFFFF80',
          borderWidth: 1,
          backgroundColor: secondColorFill || 'rgba(70, 21, 92, 0.6)',
          fill: true,
          pointBackgroundColor: '#006197',
          pointBorderColor: secondColor || '#228949',
          pointRadius: 4,
          pointHoverRadius: 4,
          pointLabels: false,
        },
      ],
    }),
    [lbls, ds1, ds2, color, secondColor, colorFill, secondColorFill],
  );

  return { data, labels: lbls, options };
};
